import Vue from 'vue'
import App from './App.vue'
import './plugins/vuetify'
import './plugins/elementui'
import '~/plugins/authentication'

import SocialSharing from 'vue-social-sharing'
import NProgress from 'nprogress'
import axios from 'axios'

import router from './router/index'
import store from './store/index'
import * as TYPE from '~/store/util/types'

import {getUserFromLocalStorage, getToken} from '~/utils/auth'

import './assets/style/app.styl'

import './assets/style/event_style.scss'

import './assets/style/nprogress.css'
import './assets/style/buttons.scss'
import './assets/style/add-event.scss'
import './assets/style/host-apply.scss'
import './assets/style/host-dashboard.scss'
import './assets/style/event-page.scss'
import './assets/style/map.scss'
import './assets/style/mobile-buttons.scss'
import './assets/style/modals.scss'
import './assets/style/terms-modal.scss'

import './assets/style/fa-social-icons-colored.css'

import * as Sentry from "@sentry/vue";
import {BrowserTracing} from "@sentry/tracing";

if (process.env.NODE_ENV !== 'development') {
    Sentry.init({
        Vue,
        dsn: "https://6ef02aa645be49f183e910cedfe4d730@o126806.ingest.sentry.io/279604",
        integrations: [
            new BrowserTracing({
                routingInstrumentation: Sentry.vueRouterInstrumentation(router),
                tracingOrigins: ["localhost", "wrkprty.com", /^\//],
            }),
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
    });
}

// NProgress loading bar animation configuration
NProgress.configure({
    minimum: 0.1,
    easing: 'ease',
    speed: 500,
    showSpinner: false
})

Vue.use(SocialSharing)

// Set user logged in before routes
const user = getUserFromLocalStorage()
const token = getToken()
if (token) {
    store.dispatch('setToken', token)
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
}

// don't cache api calls
axios.defaults.headers.common['Pragma'] = 'no-cache'

axios.defaults.baseURL = process.env.VUE_APP_SERVER_PROTOCOL + process.env.VUE_APP_SERVER_URL

if (user) {
    store.dispatch('setUserSocial', user)
    Sentry.setUser({ email: user.email, id: user.id });
}

router.beforeEach((to, from, next) => {
    // NProgress loading bar animation
    NProgress.start()
    NProgress.set(0.1)

    let shouldProceed = true
    let redirectPath = null
    if (to.matched.some(record => record.meta.requireAuthVerify)) {
        // console.log('requireAuthVerify')

        let token = store.getters.getToken

        if (store.getters.userIsAuthenticated) {
            axios
                .post('/api-token-verify/', {token: token})
                .catch(error => {
                    console.log('verify token fail')
                    console.error(error)
                    shouldProceed = false
                    store.commit(TYPE.SET_SNACKBAR_OPEN, true)
                    store.commit(TYPE.SET_SNACKBAR_TEXT, 'Error checking authentication')
                })
        } else {
            shouldProceed = false
            store.commit(TYPE.SET_SNACKBAR_OPEN, true)
            store.commit(
                TYPE.SET_SNACKBAR_TEXT,
                'You must be logged in to view this page'
            )
        }
    }
    if (to.matched.some(record => record.meta.requiresAuthNotActive)) {
        if (!store.getters.userIsAuthenticated) {
            shouldProceed = false
            store.commit(TYPE.SET_SNACKBAR_OPEN, true)
            store.commit(TYPE.SET_SNACKBAR_TEXT, 'This page is unavailable')
        }
    }
    if (to.matched.some(record => record.meta.redirectNonHost)) {
        if (store.getters.userIsHost) {
            console.log('User is host')
        } else if (store.getters.getHostProcessStep === 4) {
            if (
                to.name === 'host_info' ||
                to.name === 'host_apply' ||
                to.name === 'host_space'
            ) {
                shouldProceed = false
                redirectPath = 'host_progress'
                store.commit(TYPE.SET_SNACKBAR_OPEN, true)
                store.commit(TYPE.SET_SNACKBAR_TEXT, 'This page is unavailable.')
            }
        } else if (store.getters.getHostProcessStep === 3) {
            if (
                to.name === 'host_info' ||
                to.name === 'host_apply' ||
                to.name === 'host_space'
            ) {
                shouldProceed = false
                redirectPath = 'host_progress'
                store.commit(TYPE.SET_SNACKBAR_OPEN, true)
                store.commit(TYPE.SET_SNACKBAR_TEXT, 'This page is unavailable.')
            }
        } else if (store.getters.getHostProcessStep === 2) {
            if (to.name === 'host_info' || to.name === 'host_apply') {
                shouldProceed = false
                redirectPath = 'host_progress'
                store.commit(TYPE.SET_SNACKBAR_OPEN, true)
                store.commit(TYPE.SET_SNACKBAR_TEXT, 'This page is unavailable.')
            }
        } else if (to.name !== 'host_info' && to.name !== 'host_apply') {
            shouldProceed = false
            redirectPath = 'host_info'
            store.commit(TYPE.SET_SNACKBAR_OPEN, true)
            store.commit(
                TYPE.SET_SNACKBAR_TEXT,
                'You must be a host to view this page.'
            )
        }
    }
    if (to.matched.some(record => record.meta.requiresHost)) {
        if (!store.getters.userIsHost) {
            shouldProceed = false
            store.commit(TYPE.SET_SNACKBAR_OPEN, true)
            store.commit(
                TYPE.SET_SNACKBAR_TEXT,
                'You must be a host to view this page.'
            )
        }
    } else if (to.matched.some(record => record.meta.requiresNotHost)) {
        if (store.getters.userIsHost) {
            shouldProceed = false
            redirectPath = 'root'
            store.commit(TYPE.SET_SNACKBAR_OPEN, true)
            store.commit(TYPE.SET_SNACKBAR_TEXT, 'This page is unavailable.')
        }
    }
    if (to.matched.some(record => record.meta.requiresAuth)) {
        // console.log('requiresAuth')
        if (!store.getters.userIsAuthenticated) {
            shouldProceed = false
            store.commit(TYPE.SET_SNACKBAR_OPEN, true)
            store.commit(
                TYPE.SET_SNACKBAR_TEXT,
                'You must be logged in to view this page.'
            )
        } else {
            if (store.getters.user.active === false) {
                shouldProceed = false
                redirectPath = 'user_confirmation'
                store.commit(TYPE.SET_SNACKBAR_OPEN, true)
                store.commit(
                    TYPE.SET_SNACKBAR_TEXT,
                    'You must verify your information before continuing.'
                )
                next({path: '/user/confirmation'})
            }
        }
    }

    // Check if route guards are clear
    if (shouldProceed) {
        next()
    } else if (redirectPath) {
        router.replace({name: redirectPath})
        next({
            name: redirectPath,
            query: {notif: true}
        })
    } else {
        router.replace({path: '/'})
        next({
            name: 'root',
            query: {redirect: to.fullPath}
        })
    }
})

router.afterEach(() => {
    // Set loading bar to done after route change completion
    NProgress.done()
})

// GraphQL
// const apolloProvider = new VueApollo({
// 	defaultClient: apolloClient,
// })

// eslint-disable-next-line no-new
const app = new Vue({
    el: '#app',
    store,
    router,
    // provide: apolloProvider.provide(),
    components: {
        App
    },
    // render: h => h(App),
    template: '<App />'
})

if (window.Cypress) {
    // only available during E2E tests
    window.app = app
}
